import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import axiosInstance from "../../api/axios";

const DEFAULT_PAGE_SIZE = 10;

export const COMMON_FUNCTIONALITY_OPTIONS = [
  { label: "Consent Collection", value: "consent-collection" },
];

export const FILTER_FUNCTIONALITY_OPTIONS = [
  { label: "All", value: "All" },
  ...COMMON_FUNCTIONALITY_OPTIONS,
];

const toDateOnly = (date) => {
  const d = new Date(date);
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()),
  );
};

const toInputDate = (date) => {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const addMonthsUtc = (date, months) =>
  new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth() + months,
      date.getUTCDate(),
    ),
  );

const mapRecord = (record, userMap = {}) => {

  const fileName = record.data_key
    ? record.data_key.split("/").pop()
    : record.batch_id;

  const uploadedById = record.uploaded_by ?? "NA";

  return {
    batchId: record.batch_id,
    fileName,
    functionality: record.functionality,
    totalRecords: record.total_records,
    successfulRecords: record.successful_records,
    failedRecords: record.failed_records,
    issues: record.issues ?? 0,
    issuesLink: record.issues_link ?? null,
    status: record.status,
    uploadedBy: userMap[uploadedById] ?? uploadedById,
    sourceSystem: record.source_system ?? "NA",
    uploadedDateUtc: record.received_at,
    downloadLink: record.download_link ?? null,
  };
};

const todayUtc = toDateOnly(new Date());
const defaultFromUtc = addMonthsUtc(todayUtc, -3);

const fetchUserDisplayName = async (id) => {
  try {
    const response = await axiosInstance.get(`/entra_user_info/${id}`);
    if (response?.data?.statusCode == 200 && response?.data?.display_name) {
      return { id, displayName: response.data.display_name };
    }
  } catch (error) {
    console.error(`Failed to fetch user info for ${id}:`, error);
  }

  return { id, displayName: null };
};

export default function useFileManagementData() {
  const [allData, setAllData] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const controller_code = useSelector((state) => state.controllers.controller);
  const customer_scope = useSelector((state) => state.controller.scope);
  const [filters, setFilters] = useState({
    functionalityName: "All",
    startDate: toInputDate(defaultFromUtc),
    endDate: toInputDate(todayUtc),
  });
  const [loading, setLoading] = useState(false);

  const abortRef = useRef(null);

  const fetchFileManagementData = useCallback(async () => {
    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      setLoading(true);

      const params = {
        startDate: `${filters.startDate} 00:00:00.000`,
        endDate: `${filters.endDate} 23:59:59.999`,
        controller_code,
        customer_scope,
      };

      if (filters.functionalityName && filters.functionalityName !== "All") {
        params.functionalityName = filters.functionalityName;
      }

      const response = await axiosInstance.get("/files", {
        params,
        signal: controller.signal,
      });

      if (controller.signal.aborted) return;

      const items = response?.data?.data?.items ?? [];

      const uploadedByIds = [
        ...new Set(
          items.map((item) => item?.uploaded_by).filter(Boolean),
        ),
      ];

      const users = await Promise.all(
        uploadedByIds.map((id) => fetchUserDisplayName(id)),
      );

      if (controller.signal.aborted) return;

      const userMap = users.reduce((acc, user) => {
        if (user?.displayName) {
          acc[user.id] = user.displayName;
        }
        return acc;
      }, {});

      setAllData(items.map((item) => mapRecord(item, userMap)));
    } catch (error) {
      // Ignore cancellation errors
      if (
        error &&
        (error.name === "CanceledError" || error.code === "ERR_CANCELED")
      ) {
        return;
      }

      console.error("Failed to fetch file management data:", error);
      setAllData([]);
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [
    filters.startDate,
    filters.endDate,
    filters.functionalityName,
    controller_code,
    customer_scope,
  ]);

  useEffect(() => {
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    fetchFileManagementData();
  }, [fetchFileManagementData]);

  const totalCount = allData.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const data = useMemo(() => {
    const safePage = Math.min(page, totalPages);
    const start = (safePage - 1) * pageSize;
    return allData.slice(start, start + pageSize);
  }, [allData, page, pageSize, totalPages]);

  const handleFirstPageChange = () => setPage(1);
  const handlePrevPageChange = () => setPage((prev) => Math.max(1, prev - 1));
  const handleNextPageChange = () =>
    setPage((prev) => (prev < totalPages ? prev + 1 : prev));
  const handleLastPageChange = () => setPage(totalPages);

  const setPageSizeState = (nextPageSize) => {
    setPageSize(nextPageSize);
    setPage(1);
  };

  const updateFunctionality = (value) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, functionalityName: value || "All" }));
  };

  const updateFromDate = (value) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, startDate: value }));
  };

  const updateToDate = (value) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, endDate: value }));
  };

  const resetFilters = () => {
    setPage(1);
    setFilters({
      functionalityName: "All",
      startDate: toInputDate(defaultFromUtc),
      endDate: toInputDate(todayUtc),
    });
  };

  const resetDateFilters = () => {
    setPage(1);
    setFilters((prev) => ({
      ...prev,
      startDate: toInputDate(defaultFromUtc),
      endDate: toInputDate(todayUtc),
    }));
  };

  return {
    data,
    loading,
    page,
    pageSize,
    totalCount,
    totalPages,
    filters,
    functionalityOptions: FILTER_FUNCTIONALITY_OPTIONS,
    updateFunctionality,
    updateFromDate,
    updateToDate,
    resetFilters,
    resetDateFilters,
    handleFirstPageChange,
    handlePrevPageChange,
    handleNextPageChange,
    handleLastPageChange,
    setPageSize: setPageSizeState,
    refetch: fetchFileManagementData,
  };
}